import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Card, Alert,
  TextField, InputAdornment, Chip, IconButton, Tooltip, Button,
  CircularProgress, Snackbar,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalculateIcon from '@mui/icons-material/Calculate';

import { useAuth } from '../../context/AuthContext';
import { useCupones, useRecalcularInteresesMasivo } from '../../hooks/useCupones';
import { formatCurrency, formatDate } from '../../utils/format';
import {
  type Cupon,
  type CuponEstado,
  getEstadoCuponLabel,
  getEstadoCuponColor,
} from '../../types/cupon';

// ── Component ─────────────────────────────────────────────────────────────────

export default function CuponesList() {
  const navigate = useNavigate();
  const { effectiveRole, effectiveEmpresaId } = useAuth();
  const isAdminEmpresa = effectiveRole === 'admin_empresa';
  const puedeRecalcular =
    effectiveRole === 'admin_general' || effectiveRole === 'operador';

  const { data: cupones = [], isError, isLoading } = useCupones(
    isAdminEmpresa ? (effectiveEmpresaId ?? undefined) : undefined
  );
  const recalcular = useRecalcularInteresesMasivo();

  const [busqueda, setBusqueda] = useState('');
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const handleRecalcular = async () => {
    try {
      const r = await recalcular.mutateAsync();
      setSnackbar(
        `Recálculo OK — Revisados: ${r.revisados}, actualizados: ${r.cambiados}`
      );
    } catch (e) {
      setSnackbar('Error al recalcular: ' + (e as Error).message);
    }
  };

  // ── Filtro cliente ────────────────────────────────────────────────────────

  const filtered = cupones.filter((c) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return (
      c.empresa?.razon_social?.toLowerCase().includes(q) ||
      c.periodo?.periodo?.toLowerCase().includes(q) ||
      c.codigo?.toLowerCase().includes(q)
    );
  });

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns: GridColDef<Cupon>[] = [
    ...(!isAdminEmpresa
      ? [
          {
            field: 'empresa',
            headerName: 'Empresa',
            flex: 2,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams<Cupon>) =>
              params.row.empresa?.razon_social ?? '-',
          } as GridColDef<Cupon>,
        ]
      : []),
    {
      field: 'periodo',
      headerName: 'Período',
      width: 110,
      renderCell: (params: GridRenderCellParams<Cupon>) =>
        params.row.periodo?.periodo ?? '-',
    },
    {
      field: 'codigo',
      headerName: 'Código',
      width: 220,
      renderCell: (params: GridRenderCellParams<Cupon>) => (
        <Typography variant="body2" fontFamily="monospace" fontSize={13}>
          {params.row.codigo}
        </Typography>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 120,
      renderCell: (params: GridRenderCellParams<Cupon, CuponEstado>) => (
        <Chip
          label={getEstadoCuponLabel(params.row.estado)}
          color={getEstadoCuponColor(params.row.estado)}
          size="small"
        />
      ),
    },
    {
      field: 'monto_total',
      headerName: 'Total',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<Cupon>) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(params.row.monto_total)}
        </Typography>
      ),
    },
    {
      field: 'fecha_vencimiento',
      headerName: 'Vencimiento',
      width: 120,
      renderCell: (params: GridRenderCellParams<Cupon>) => (
        <Typography
          variant="body2"
          color={params.row.estado === 'vencido' ? 'error' : 'inherit'}
        >
          {formatDate(params.row.fecha_vencimiento)}
        </Typography>
      ),
    },
    {
      field: 'acciones',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Cupon>) => (
        <Tooltip title="Ver cupón">
          <IconButton
            size="small"
            onClick={() => navigate(`/cupones/${params.row.id}`)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Cupones
        </Typography>
        {puedeRecalcular && (
          <Tooltip title="Recalcula intereses por mora de los cupones pendientes/vencidos">
            <Button
              size="small"
              variant="outlined"
              startIcon={
                recalcular.isPending ? <CircularProgress size={16} /> : <CalculateIcon />
              }
              onClick={handleRecalcular}
              disabled={recalcular.isPending}
            >
              Recalcular intereses
            </Button>
          </Tooltip>
        )}
      </Stack>

      {/* Filtro */}
      <TextField
        size="small"
        placeholder="Buscar por empresa, período o código…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Error */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los cupones.
        </Alert>
      )}

      {/* Grid */}
      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Card>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnackbar(null)}
      />
    </Box>
  );
}
